/**
 * Error Log Admin Page
 *
 * Displays database errors logged by the system for monitoring.
 * Helps detect silent SQL errors like column name mismatches.
 *
 * Features:
 * - View errors with filters (type, source, date range)
 * - Expand rows to see full error details (query, stack trace)
 * - Delete individual errors or clear old entries
 * - Stats summary showing error counts by type
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Tooltip,
  Pagination,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { api } from '../../helpers/apiUtils';
import moment from 'moment';

interface ErrorEntry {
  id: number;
  type: string;
  source: string;
  message: string;
  query: string | null;
  stack: string | null;
  context: Record<string, unknown> | null;
  createdAt: string;
}

interface ErrorStats {
  byType: { error_type: string; count: number; latest: string }[];
  last24h: number;
}

const ErrorLog: React.FC = () => {
  const [errors, setErrors] = useState<ErrorEntry[]>([]);
  const [stats, setStats] = useState<ErrorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [filterType, setFilterType] = useState<string>('');
  const [filterSource, setFilterSource] = useState<string>('');

  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const loadErrors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filterType) params.append('type', filterType);
      if (filterSource) params.append('source', filterSource);
      params.append('limit', String(pageSize));
      params.append('offset', String((page - 1) * pageSize));

      const response = await api.get(`/api/admin/error-log?${params.toString()}`);
      setErrors(response.data.data.errors);
      setTotal(response.data.data.pagination.total);
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      console.error('Error loading error log:', err);
      setError(error.response?.data?.error || error.message || 'Failed to load error log');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterSource, page]);

  const loadStats = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/error-log/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  useEffect(() => {
    loadErrors();
    loadStats();
  }, [loadErrors, loadStats]);

  // Auto-clear success message
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/api/admin/error-log/${id}`);
      setSuccess('Fehler wurde geloescht');
      loadErrors();
      loadStats();
    } catch (err) {
      console.error('Error deleting:', err);
      setError('Loeschen fehlgeschlagen');
    }
  };

  const handleClearOld = async () => {
    if (!window.confirm('Alle Fehler loeschen die aelter als 30 Tage sind?')) return;

    try {
      const response = await api.delete('/api/admin/error-log/clear-old');
      setSuccess(response.data.data.message);
      loadErrors();
      loadStats();
    } catch (err) {
      console.error('Error clearing old:', err);
      setError('Loeschen fehlgeschlagen');
    }
  };

  const handleClearAll = async () => {
    if (
      !window.confirm(
        'ACHTUNG: Alle Fehler unwiderruflich loeschen? Diese Aktion kann nicht rueckgaengig gemacht werden!',
      )
    )
      return;

    try {
      const response = await api.delete('/api/admin/error-log/clear-all');
      setSuccess(response.data.data.message);
      loadErrors();
      loadStats();
    } catch (err) {
      console.error('Error clearing all:', err);
      setError('Loeschen fehlgeschlagen');
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getTypeColor = (type: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (type) {
      case 'sql':
        return 'error';
      case 'scheduler':
        return 'warning';
      case 'api':
        return 'info';
      default:
        return 'default';
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Fehler-Log
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadErrors();
              loadStats();
            }}
          >
            Aktualisieren
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CleaningServicesIcon />}
            onClick={handleClearOld}
          >
            Alte loeschen (30+ Tage)
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={handleClearAll}
          >
            Alle loeschen
          </Button>
        </Stack>
      </Box>

      {/* Stats Summary */}
      {stats && (
        <Box mb={3}>
          <Stack direction="row" spacing={2}>
            <Paper sx={{ p: 2, minWidth: 150 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Letzte 24h
              </Typography>
              <Typography variant="h4" color={stats.last24h > 0 ? 'error.main' : 'success.main'}>
                {stats.last24h}
              </Typography>
            </Paper>
            {stats.byType.map((s) => (
              <Paper key={s.error_type} sx={{ p: 2, minWidth: 120 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {s.error_type}
                </Typography>
                <Typography variant="h5">{s.count}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {moment(s.latest).fromNow()}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Filters */}
      <Box mb={2}>
        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Typ</InputLabel>
            <Select
              value={filterType}
              label="Typ"
              onChange={(e) => {
                setFilterType(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="sql">SQL</MenuItem>
              <MenuItem value="api">API</MenuItem>
              <MenuItem value="scheduler">Scheduler</MenuItem>
              <MenuItem value="general">Allgemein</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Source"
            value={filterSource}
            onChange={(e) => {
              setFilterSource(e.target.value);
              setPage(1);
            }}
            placeholder="z.B. WebinarFollowupService"
            sx={{ width: 300 }}
          />
        </Stack>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : errors.length === 0 ? (
        <Alert severity="success">Keine Fehler gefunden</Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                      width: 40,
                    }}
                  />
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                  >
                    Zeit
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                  >
                    Typ
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                  >
                    Source
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                    }}
                  >
                    Nachricht
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: '#e0e0e0 !important',
                      fontWeight: 'bold',
                      width: 60,
                    }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((err) => (
                  <React.Fragment key={err.id}>
                    <TableRow hover onClick={() => toggleExpand(err.id)} sx={{ cursor: 'pointer' }}>
                      <TableCell>
                        <IconButton size="small">
                          {expandedRows.has(err.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {moment(err.createdAt).format('DD.MM.YY HH:mm')}
                      </TableCell>
                      <TableCell>
                        <Chip label={err.type} color={getTypeColor(err.type)} size="small" />
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <Tooltip title={err.source}>
                          <span>{err.source}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        <Tooltip title={err.message}>
                          <span>
                            {err.message.substring(0, 100)}
                            {err.message.length > 100 ? '...' : ''}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(err.id);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Details */}
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          p: 0,
                          borderBottom: expandedRows.has(err.id) ? undefined : 'none',
                        }}
                      >
                        <Collapse in={expandedRows.has(err.id)} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Vollstaendige Nachricht:
                            </Typography>
                            <Paper sx={{ p: 1, mb: 2, backgroundColor: '#fff' }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {err.message}
                              </Typography>
                            </Paper>

                            {err.query && (
                              <>
                                <Typography variant="subtitle2" gutterBottom>
                                  SQL Query:
                                </Typography>
                                <Paper
                                  sx={{
                                    p: 1,
                                    mb: 2,
                                    backgroundColor: '#fff',
                                    overflow: 'auto',
                                    maxHeight: 200,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {err.query}
                                  </Typography>
                                </Paper>
                              </>
                            )}

                            {err.context && (
                              <>
                                <Typography variant="subtitle2" gutterBottom>
                                  Context:
                                </Typography>
                                <Paper
                                  sx={{
                                    p: 1,
                                    mb: 2,
                                    backgroundColor: '#fff',
                                    overflow: 'auto',
                                    maxHeight: 150,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {JSON.stringify(err.context, null, 2)}
                                  </Typography>
                                </Paper>
                              </>
                            )}

                            {err.stack && (
                              <>
                                <Typography variant="subtitle2" gutterBottom>
                                  Stack Trace:
                                </Typography>
                                <Paper
                                  sx={{
                                    p: 1,
                                    backgroundColor: '#fff',
                                    overflow: 'auto',
                                    maxHeight: 200,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      fontSize: '0.7rem',
                                      color: '#666',
                                    }}
                                  >
                                    {err.stack}
                                  </Typography>
                                </Paper>
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {total} Fehler insgesamt
          </Typography>
        </>
      )}
    </Box>
  );
};

export default ErrorLog;
